/**
 * Gate 1 — Step 1: Trust Attribute Weighting.
 *
 * Self-contained — owns its own fetch / autosave / generate logic and
 * exposes only what the parent page needs to gate Step 2:
 *   onWeightsReady(boolean) → fires whenever the (aiRecommendation && total===100)
 *                             status changes, so the parent can unlock Step 2.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputBase,
  LinearProgress,
  Slider,
  SliderThumb,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import muiSliderThumbIcon from "../../asserts/logo/MuiSliderThumb.svg";
import muiSliderMarkIcon from "../../asserts/logo/MuiSliderMark.svg";
import { INTAKE_COLORS } from "../../theme/intakeTokens";
import {
  fetchTrustWeights,
  generateTrustWeights,
  saveTrustWeights,
} from "../../services/trustWeightsClient";
import { postGate0FeaturesEnabled } from "../../config/productFeatures";
import AiProvenanceLink from "../../components/AiProvenanceLink";
import {
  TRUST_ATTRIBUTES,
  TRUST_ATTRIBUTE_DESCRIPTIONS,
  ZERO_TRUST_WEIGHTS,
  trustAttributeLabel,
} from "../../config/trustAttributes";

const POST_GATE0_DISABLED = !postGate0FeaturesEnabled();
const log = (...args) => console.log("[GATE1.STEP1.UI]", ...args);

const ATTRIBUTES = TRUST_ATTRIBUTES.map((key) => ({
  key,
  label: trustAttributeLabel(key),
  desc: TRUST_ATTRIBUTE_DESCRIPTIONS[key],
}));

/** Figma — Test Cases “56 results” meta line (Lato 13 / 100%, Grey-70 + Grey-50) */
const META_COUNT_LINE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 13,
  lineHeight: "100%",
  letterSpacing: 0,
  width: "fit-content",
  maxWidth: "100%",
  ml: { xs: 0, sm: "auto" },
  flexShrink: 0,
  textAlign: { xs: "left", sm: "right" },
};

/**
 * Baseline for the per-row BASELINE column. Starts at 0 for every attribute and
 * becomes the AI-generated weights once Generate has run — whatever the system
 * generates is set as the new baseline (see `baselineWeights` in the component).
 */
const ZERO_BASELINE_WEIGHTS = ZERO_TRUST_WEIGHTS;

const SLIDER_MIN = 0;
const SLIDER_MAX = 40;
const SLIDER_STEP = 5;
const WEIGHT_MIN = 0;
const WEIGHT_MAX = 40;

const SLIDER_MARKS = Array.from({ length: SLIDER_MAX / SLIDER_STEP + 1 }, (_, i) => {
  const value = i * SLIDER_STEP;
  return {
    value,
    ...(value === SLIDER_MIN || value === SLIDER_MAX ? { label: String(value) } : {}),
  };
});

function clampWeight(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return WEIGHT_MIN;
  return Math.max(WEIGHT_MIN, Math.min(WEIGHT_MAX, Math.round(n)));
}

// Target trust index — same range the backend enforces (70–99).
const TARGET_MIN = 70;
const TARGET_MAX = 99;
const TARGET_DEFAULT = 85;
const TARGET_MARKS = [70, 75, 80, 85, 90, 95, 99].map((value) => ({
  value,
  ...(value === TARGET_MIN || value === TARGET_MAX ? { label: String(value) } : {}),
}));

function clampTarget(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return TARGET_DEFAULT;
  return Math.max(TARGET_MIN, Math.min(TARGET_MAX, Math.round(n)));
}

/**
 * Slider look shared by the attribute rows and the target-trust-index row
 * (dimensions, rail/track colours, thumb + mark artwork). The attribute rows
 * are read-only indicators (pointerEvents none); the target row overrides that.
 */
const TRUST_SLIDER_SX = {
  color: "#0046B8",
  width: "100%",
  maxWidth: "100%",
  height: 4,
  padding: "13px 18px",
  overflow: "visible",
  boxSizing: "border-box",
  pointerEvents: "none",
  "& .MuiSlider-rail": {
    left: 0,
    right: 0,
    width: "100%",
    height: 4,
    borderRadius: "12px",
    opacity: 1,
    bgcolor: "#E5E7EB",
  },
  "& .MuiSlider-track": {
    border: "none",
    height: 4,
    borderRadius: "12px",
    bgcolor: "#0046B8",
  },
  "& .MuiSlider-thumb": {
    width: 30,
    height: 30,
    bgcolor: "transparent",
    border: "none",
    boxShadow: "none",
    backgroundImage: `url(${muiSliderThumbIcon})`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "30px 30px",
    pointerEvents: "auto",
    cursor: "default",
    "&::before": { display: "none" },
    "&:hover, &.Mui-focusVisible, &.Mui-active": {
      boxShadow: "none",
    },
  },
  "& .MuiSlider-mark": {
    width: 2,
    height: 6,
    borderRadius: 0,
    opacity: 1,
    bgcolor: "transparent",
    backgroundImage: `url(${muiSliderMarkIcon})`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "2px 6px",
  },
  "& .MuiSlider-markActive": {
    bgcolor: "transparent",
    opacity: 1,
  },
  "& .MuiSlider-markLabel": {
    fontFamily: "Lato, sans-serif",
    fontSize: 11,
    lineHeight: "16px",
    color: "#98A2B3",
    transform: "translateX(-50%)",
  },
};

const TARGET_SLIDER_SX = {
  ...TRUST_SLIDER_SX,
  pointerEvents: "auto",
  "& .MuiSlider-thumb": { ...TRUST_SLIDER_SX["& .MuiSlider-thumb"], cursor: "pointer" },
};

function DeltaFromBaseline({ delta }) {
  if (delta > 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: "#067647", minWidth: 52 }}>
        <TrendingUpIcon sx={{ fontSize: 16 }} />
        <Typography fontSize={12} fontWeight={700}>+{delta}</Typography>
      </Box>
    );
  }
  if (delta < 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: "#B42318", minWidth: 52 }}>
        <TrendingDownIcon sx={{ fontSize: 16 }} />
        <Typography fontSize={12} fontWeight={700}>{delta}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#98A2B3", minWidth: 52 }}>
      <Typography fontSize={12} fontWeight={600}>—</Typography>
      <Typography fontSize={12} fontWeight={600}>0</Typography>
    </Box>
  );
}

/** Read-only slider thumb — tooltip with current weight only on the thumb handle */
function WeightSliderThumb({ weight, tooltipDisabled, ...props }) {
  const thumb = <SliderThumb {...props} />;
  if (tooltipDisabled) return thumb;
  return (
    <Tooltip title={`${weight}%`} placement="top" arrow enterTouchDelay={0}>
      {thumb}
    </Tooltip>
  );
}

function WeightInput({ value, disabled, focused, onFocus, onChange, onBlur, onKeyDown }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: `1px solid ${focused ? "#1A6EF5" : "#D0D5DD"}`,
        borderRadius: "8px",
        px: 1.25,
        py: 0.75,
        minWidth: 72,
        bgcolor: "#fff",
        boxShadow: focused ? "0 0 0 3px rgba(26, 110, 245, 0.12)" : "none",
      }}
    >
      <InputBase
        value={value}
        disabled={disabled}
        onFocus={onFocus}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        inputProps={{
          inputMode: "numeric",
          pattern: "[0-9]*",
          "aria-label": "Weight percentage",
          style: { textAlign: "right", fontSize: 14, fontWeight: 700, width: 28, padding: 0 },
        }}
        sx={{ flex: 1 }}
      />
      <Typography fontSize={13} color="#667085" fontWeight={500} sx={{ ml: 0.25 }}>
        %
      </Typography>
    </Box>
  );
}

export default function Step1TrustAttributes({ projectId, onWeightsReady }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const [weights, setWeights] = useState(null);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [targetTrustIndex, setTargetTrustIndex] = useState(85);
  const [rationale, setRationale] = useState("");
  const [projectName, setProjectName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAttr, setSavingAttr] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [editingAttr, setEditingAttr] = useState(null);
  const [draftValue, setDraftValue] = useState("");

  const total = useMemo(
    () => (weights ? Object.values(weights).reduce((a, n) => a + Number(n || 0), 0) : 0),
    [weights]
  );
  const isValidTotal = total === 100;
  const ready = Boolean(aiRecommendation) && isValidTotal;

  // Baseline = whatever the system generated (the AI recommendation). Before any
  // generation has run it stays at 0 for every attribute.
  const baselineWeights = useMemo(() => {
    const rec = aiRecommendation?.recommendedWeights;
    if (rec && typeof rec === "object") {
      return ATTRIBUTES.reduce((acc, { key }) => {
        acc[key] = Number(rec[key] ?? 0);
        return acc;
      }, {});
    }
    return { ...ZERO_BASELINE_WEIGHTS };
  }, [aiRecommendation]);

  // The three highest-weighted attributes in the AI recommendation — what the
  // summary paragraph names as this application's top risk drivers. Derived,
  // never hardcoded, so the sentence always matches the weights shown below it.
  const topDrivers = useMemo(
    () =>
      ATTRIBUTES.map(({ key, label }) => ({ key, label, weight: baselineWeights[key] || 0 }))
        .filter((a) => a.weight > 0)
        .sort((a, b) => b.weight - a.weight)
        .slice(0, 3),
    [baselineWeights]
  );

  // Tell the parent every time the unlock condition flips.
  const lastReadyRef = useRef(null);
  useEffect(() => {
    if (lastReadyRef.current !== ready && typeof onWeightsReady === "function") {
      lastReadyRef.current = ready;
      onWeightsReady(ready);
    }
  }, [ready, onWeightsReady]);

  // ── Initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    log("mount → fetchTrustWeights start, projectId:", projectId);
    (async () => {
      const t0 = performance.now();
      try {
        setLoading(true);
        const res = await fetchTrustWeights(projectId);
        if (!res?.success) {
          log("✗ fetch failed:", res?.error);
          setError(res?.error || "Failed to load trust weights.");
          return;
        }
        const d = res.data;
        log(
          "✓ fetchTrustWeights",
          `${Math.round(performance.now() - t0)}ms`,
          "— hasAiRecommendation:", Boolean(d.aiRecommendation),
          "trustWeightsValid:", d.trustWeightsValid,
          "total:", d.trustWeightsTotal,
        );
        setWeights(d.trustWeights);
        setAiRecommendation(d.aiRecommendation || null);
        setTargetTrustIndex(d.targetTrustIndex || 85);
        setRationale(d.rationale || "");
        setProjectName(d.project_name || d.projectName || "");
        setLastSavedAt(d.lastUpdatedAt || null);
      } catch (e) {
        log("✗ fetch threw:", e.message);
        setError(e.response?.data?.error || e.message || "Failed to load trust weights.");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  // ── Autosave on manual weight commit (no rebalance) ────────────────────
  const saveTimerRef = useRef(null);
  const persistWeights = useCallback(
    async (next, attrBeingSaved = null, targetOverride = null) => {
      const t0 = performance.now();
      log("autosave → PUT /trust-weights", attrBeingSaved ? `(attr=${attrBeingSaved})` : "", "values:", next);
      try {
        setSaving(true);
        if (attrBeingSaved) setSavingAttr(attrBeingSaved);
        const res = await saveTrustWeights(projectId, {
          trustWeights: next,
          targetTrustIndex: targetOverride ?? targetTrustIndex,
          rationale,
        });
        if (res?.success && res.data?.trustWeights) {
          log(
            "✓ saved",
            `${Math.round(performance.now() - t0)}ms`,
            "— total:", res.data.trustWeightsTotal,
            "valid:", res.data.trustWeightsValid,
          );
          setWeights(res.data.trustWeights);
          setLastSavedAt(res.data.lastUpdatedAt || new Date().toISOString());
        }
      } catch (e) {
        log("✗ save threw:", e.message);
        setError(e.response?.data?.error || e.message || "Auto-save failed.");
      } finally {
        setSaving(false);
        setSavingAttr(null);
      }
    },
    [projectId, targetTrustIndex, rationale]
  );

  const commitWeightInput = useCallback(
    (attr) => {
      const nextValue = clampWeight(draftValue);
      setWeights((prev) => {
        const next = { ...(prev || {}), [attr]: nextValue };
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => persistWeights(next, attr), 150);
        return next;
      });
      setEditingAttr(null);
      setDraftValue("");
    },
    [draftValue, persistWeights]
  );

  const handleInputFocus = (attr, current) => () => {
    setEditingAttr(attr);
    setDraftValue(String(current));
  };

  const handleInputChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    setDraftValue(raw);
  };

  const handleInputBlur = (attr) => () => {
    commitWeightInput(attr);
  };

  const handleInputKeyDown = (attr) => (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitWeightInput(attr);
    }
    if (e.key === "Escape") {
      setEditingAttr(null);
      setDraftValue("");
    }
  };

  const handleGenerate = async () => {
    const t0 = performance.now();
    log("user clicked Generate → POST /trust-weights/generate");
    try {
      setGenerating(true);
      const res = await generateTrustWeights(projectId);
      if (!res?.success) {
        log("✗ generate failed:", res?.error);
        setError(res?.error || "Generation failed.");
        return;
      }
      log(
        "✓ generated",
        `${Math.round(performance.now() - t0)}ms`,
        "— target:", res.data.targetTrustIndex,
        "weights:", res.data.trustWeights,
      );
      setAiRecommendation(res.data.aiRecommendation);
      setWeights(res.data.trustWeights);
      setTargetTrustIndex(res.data.targetTrustIndex);
      setRationale(res.data.rationale);
      setLastSavedAt(new Date().toISOString());
      setToast("Trust weights generated from the full intake summary.");
    } catch (e) {
      log("✗ generate threw:", e.message);
      setError(e.response?.data?.error || e.message || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  // ── Target trust index (user-adjustable, AI value is the baseline) ──────
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetDraft, setTargetDraft] = useState("");

  const aiTargetBaseline = aiRecommendation?.targetTrustIndex != null
    ? clampTarget(aiRecommendation.targetTrustIndex)
    : TARGET_DEFAULT;
  const currentTarget = clampTarget(targetTrustIndex);
  const targetDelta = currentTarget - aiTargetBaseline;

  const handleTargetSliderChange = (_e, value) => {
    setTargetTrustIndex(clampTarget(Array.isArray(value) ? value[0] : value));
  };

  const handleTargetSliderCommit = (_e, value) => {
    const next = clampTarget(Array.isArray(value) ? value[0] : value);
    setTargetTrustIndex(next);
    persistWeights(weights || {}, "target", next);
  };

  const commitTargetInput = () => {
    const next = clampTarget(targetDraft === "" ? currentTarget : targetDraft);
    setEditingTarget(false);
    setTargetDraft("");
    setTargetTrustIndex(next);
    if (next !== currentTarget) persistWeights(weights || {}, "target", next);
  };

  const handleTargetKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitTargetInput();
    }
    if (e.key === "Escape") {
      setEditingTarget(false);
      setTargetDraft("");
    }
  };

  const handleResetToBaseline = () => {
    // The baseline is the AI-generated set — nothing to reset to before Generate.
    if (!aiRecommendation) return;
    const next = { ...baselineWeights };
    setWeights(next);
    setTargetTrustIndex(aiTargetBaseline);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persistWeights(next, null, aiTargetBaseline), 150);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  // Keep the Step 1 card chrome while fetching so we don't flash a blank bordered box.
  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E6E6E6",
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          mb: 3,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <Typography
          fontSize={11}
          fontWeight={700}
          color="#888"
          sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          Step 1
        </Typography>
        <Typography variant="h6" fontWeight={800}>
          Trust Attribute Weighting
        </Typography>
        <Typography fontSize={13} color="#666" mt={0.5}>
          Configure enterprise trust priorities by adjusting the relative weights assigned to each
          trust attribute.
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mt: 3,
            py: 1,
          }}
        >
          <CircularProgress size={22} />
          <Typography fontSize={13} color="#667085">
            Loading trust attribute weights…
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ bgcolor: "#fff", border: "1px solid #E6E6E6", borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 3, width: "100%", minWidth: 0, boxSizing: "border-box" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
            flexWrap: { xs: "wrap", sm: "nowrap" },
            minWidth: 0,
            width: "100%",
          }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
            {/* Title + Canonical flag — Figma frame: gap 8px */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                flexWrap: "wrap",
                gap: "8px",
                width: "fit-content",
                maxWidth: "100%",
                minWidth: 0,
                minHeight: 29,
                opacity: 1,
              }}
            >
              <Typography
                component="h2"
                sx={{
                  flex: "0 1 auto",
                  width: "auto",
                  maxWidth: "100%",
                  minWidth: 0,
                  height: 29,
                  m: 0,
                  opacity: 1,
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 700,
                  fontStyle: "Bold",
                  fontSize: "24px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#383838",
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                }}
              >
                Step 1 — Trust Attribute Weighting
              </Typography>
              {/* Canonical flag — immediately after title (Figma: bg #F5EEFF, text #7F2CE8) */}
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  height: 24,
                  minWidth: 73,
                  boxSizing: "border-box",
                  borderRadius: "12px",
                  gap: "10px",
                  px: "8px",
                  bgcolor: "#F5EEFF",
                  opacity: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 57,
                    height: 24,
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 700,
                    fontStyle: "Bold",
                    fontSize: "13px",
                    lineHeight: "24px",
                    letterSpacing: "0%",
                    textAlign: "center",
                    color: "#7F2CE8",
                    whiteSpace: "nowrap",
                  }}
                >
                  Canonical
                </Box>
              </Box>
              <AiProvenanceLink projectId={projectId} steps={["Trust weights"]} refreshKey={generating} />
            </Box>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              minWidth: 0,
              width: "fit-content",
              maxWidth: "100%",
              textAlign: { xs: "left", sm: "right" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                gap: "8px",
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  width: 91,
                  height: 21,
                  m: 0,
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 700,
                  fontStyle: "Bold",
                  fontSize: "16px",
                  lineHeight: "21px",
                  letterSpacing: "0%",
                  color: "#707070",
                  whiteSpace: "nowrap",
                }}
              >
                Total Weight
              </Typography>
              {/* Percentage Flag — Figma Flag / Success (Default) */}
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  minWidth: 50,
                  height: 24,
                  boxSizing: "border-box",
                  borderRadius: "12px",
                  gap: "10px",
                  px: "8px",
                  bgcolor: isValidTotal ? "#E8FCF3" : "#FEF3F2",
                  opacity: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 34,
                    height: 24,
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 700,
                    fontStyle: "Bold",
                    fontSize: "13px",
                    lineHeight: "24px",
                    letterSpacing: "0%",
                    textAlign: "center",
                    verticalAlign: "middle",
                    color: isValidTotal ? "#00754E" : "#B42318",
                    whiteSpace: "nowrap",
                  }}
                >
                  {total}%
                </Box>
              </Box>
            </Box>
            <Typography component="span" sx={META_COUNT_LINE_SX}>
              <Box component="span" sx={{ fontWeight: 700, color: "#383838" }}>
                {ATTRIBUTES.length}
              </Box>{" "}
              <Box component="span" sx={{ fontWeight: 400, color: "#707070" }}>
                attributes
              </Box>
              <Box component="span" sx={{ fontWeight: 400, color: "#707070" }}>
                {" "}
                | Platform baseline
              </Box>
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        <Typography
          sx={{
            mt: 1,
            mb: 0,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            minHeight: 42,
            opacity: 1,
            fontFamily: "Lato, sans-serif",
            fontWeight: 400,
            fontStyle: "normal",
            fontSize: "14px",
            lineHeight: "21px",
            letterSpacing: "0%",
            color: "#555555",
          }}
        >
          <Box component="span" sx={{ display: "block" }}>
            Configure enterprise trust priorities by adjusting the relative weights assigned to each
            trust attribute.
          </Box>
          <Box
            component="span"
            sx={{
              display: "block",
              whiteSpace: "normal",
            }}
          >
            Suggested platform baseline values can be modified based on business context, risk
            posture, and regulatory expectations. Total weight must equal 100%.
          </Box>
        </Typography>

        {/* Progress bar under header — Figma: 1272×6, radius 4, Success/60 #00754E */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "10px",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            mt: 2,
            mb: 2,
          }}
        >
          <LinearProgress
            variant="determinate"
            value={Math.min(total, 100)}
            sx={{
              flex: "1 1 auto",
              width: "100%",
              height: 6,
              borderRadius: "4px",
              bgcolor: "#EEF0F2",
              "& .MuiLinearProgress-bar": {
                bgcolor: isValidTotal ? "#00754E" : "#B42318",
                borderRadius: "4px",
              },
            }}
          />

          {/* Percentage filled — Figma Flag / Success (Default), after progress bar */}
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              minWidth: 50,
              height: 24,
              boxSizing: "border-box",
              borderRadius: "12px",
              gap: "10px",
              px: "8px",
              bgcolor: isValidTotal ? "#E8FCF3" : "#FEF3F2",
              opacity: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 34,
                height: 24,
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontStyle: "Bold",
                fontSize: "13px",
                lineHeight: "24px",
                letterSpacing: "0%",
                textAlign: "center",
                verticalAlign: "middle",
                color: isValidTotal ? "#00754E" : "#B42318",
                whiteSpace: "nowrap",
              }}
            >
              {total}%
            </Box>
          </Box>
        </Box>

        {!aiRecommendation && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              bgcolor: INTAKE_COLORS.primary20,
              borderRadius: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
            }}
          >
            <AutoAwesomeIcon sx={{ color: "#1A6EF5" }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontSize={13} fontWeight={700}>
                Let ValidAIte recommend weights based on the FULL Gate 0 intake.
              </Typography>
              <Typography fontSize={12} color="#555">
                We pass the application under test plus every captured intake answer to the LLM,
                so the recommendation reflects what was actually said during the chat.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={POST_GATE0_DISABLED || generating}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#1A6EF5",
                alignSelf: { xs: "stretch", sm: "center" },
                flexShrink: 0,
                "&:hover": { bgcolor: "#1251c0" },
              }}
            >
              {generating ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Generate ideal weights"}
            </Button>
          </Box>
        )}

        {aiRecommendation && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              mb: 2,
              boxSizing: "border-box",
              opacity: 1,
            }}
          >
            {/* Figma Line — above heading (Grey/20 #E6E6E6) */}
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: 0,
                border: "none",
                borderTop: "1px solid #E6E6E6",
                transform: "rotate(0deg)",
                opacity: 1,
              }}
            />

            {/* Content between the two outline lines */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                py: "16px",
                boxSizing: "border-box",
              }}
            >
              {/* Figma heading — Lato 400 / 16 / 21; % Bold 700 */}
              <Typography
                sx={{
                  m: 0,
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  minHeight: 21,
                  height: "auto",
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 400,
                  fontStyle: "Regular",
                  fontSize: "16px",
                  lineHeight: "21px",
                  letterSpacing: "0%",
                  verticalAlign: "middle",
                  color: "#555555",
                }}
              >
                AI-recommended target trust index:{" "}
                <Box
                  component="span"
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 700,
                    fontStyle: "Bold",
                    fontSize: "16px",
                    lineHeight: "21px",
                    letterSpacing: "0%",
                    verticalAlign: "middle",
                    color: "#005BF0",
                  }}
                >
                  {aiRecommendation.targetTrustIndex}%
                </Box>
              </Typography>

              {/* Figma description — fixed copy; app name replaces 'testing demo' */}
              <Typography
                sx={{
                  m: 0,
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 400,
                  fontStyle: "Regular",
                  fontSize: "14px",
                  lineHeight: "21px",
                  letterSpacing: "0%",
                  color: "#555555",
                }}
              >
                The top risk drivers for the{" "}
                <Box
                  component="span"
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 700,
                    fontStyle: "Bold",
                    fontSize: "14px",
                    lineHeight: "21px",
                    letterSpacing: "0%",
                    color: "#555555",
                  }}
                >
                  &apos;{projectName || "application"}&apos;
                </Box>{" "}
                are{" "}
                {topDrivers.map(({ key, label }, i) => (
                  <Box component="span" key={key}>
                    {i > 0 && (i === topDrivers.length - 1 ? " and " : ", ")}
                    <Box
                      component="span"
                      sx={{
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 700,
                    fontStyle: "Bold",
                    fontSize: "14px",
                    lineHeight: "21px",
                    letterSpacing: "0%",
                    color: "#555555",
                  }}
                    >
                      {label}
                    </Box>
                  </Box>
                ))}
                . Assurance effort is concentrated there first; the remaining
                attributes are still validated, just at proportionally lower depth.
              </Typography>

              {/* Adjustable target trust index — same slider look as the attribute rows */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: { xs: 1.5, sm: 2 },
                  position: "relative",
                  bgcolor: savingAttr === "target" ? "#F0F6FF" : "transparent",
                  transition: "background-color 200ms",
                  borderRadius: 1,
                  minWidth: 0,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box
                  sx={{
                    width: { xs: "100%", sm: "clamp(140px, 22%, 208px)" },
                    maxWidth: { xs: "100%", sm: 208 },
                    minWidth: { xs: 0, sm: 140 },
                    flex: { xs: "0 0 auto", sm: "0 1 208px" },
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    boxSizing: "border-box",
                    overflow: "hidden",
                  }}
                >
                  <Typography fontWeight={700} fontSize={14} sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    Target trust index
                  </Typography>
                  <Typography fontSize={12} color="#666" sx={{ m: 0 }}>
                    Minimum overall score required to pass ({TARGET_MIN}–{TARGET_MAX}%).
                  </Typography>
                  {savingAttr === "target" && (
                    <Typography fontSize={11} color="#1A6EF5" sx={{ m: 0, fontWeight: 700 }}>
                      Auto-saving…
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1.5, md: 2, lg: 3 },
                    flex: "1 1 0",
                    minWidth: 0,
                    overflow: "visible",
                    pb: { xs: 0.5, sm: 0 },
                  }}
                >
                  <Box sx={{ flex: "1 1 0", width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", overflow: "visible" }}>
                    <Slider
                      min={TARGET_MIN}
                      max={TARGET_MAX}
                      step={1}
                      value={currentTarget}
                      marks={TARGET_MARKS}
                      size="small"
                      disabled={POST_GATE0_DISABLED}
                      aria-label="Target trust index"
                      onChange={handleTargetSliderChange}
                      onChangeCommitted={handleTargetSliderCommit}
                      components={{
                        Thumb: (thumbProps) => (
                          <WeightSliderThumb
                            {...thumbProps}
                            weight={currentTarget}
                            tooltipDisabled={POST_GATE0_DISABLED}
                          />
                        ),
                      }}
                      sx={TARGET_SLIDER_SX}
                    />
                  </Box>

                  <Box sx={{ flexShrink: 0, textAlign: "center", minWidth: 64 }}>
                    <Typography fontSize={10} fontWeight={700} color="#98A2B3" sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>
                      Baseline
                    </Typography>
                    <Typography fontSize={14} fontWeight={700} color="#344054" sx={{ mt: 0.25 }}>
                      {aiTargetBaseline}%
                    </Typography>
                  </Box>

                  <Box sx={{ flexShrink: 0 }}>
                    <DeltaFromBaseline delta={targetDelta} />
                  </Box>

                  <Box sx={{ flexShrink: 0 }}>
                    <WeightInput
                      value={editingTarget ? targetDraft : String(currentTarget)}
                      disabled={POST_GATE0_DISABLED}
                      focused={editingTarget}
                      onFocus={() => {
                        setEditingTarget(true);
                        setTargetDraft(String(currentTarget));
                      }}
                      onChange={(e) => setTargetDraft(e.target.value.replace(/[^\d]/g, ""))}
                      onBlur={commitTargetInput}
                      onKeyDown={handleTargetKeyDown}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Figma Line 5 — below content (Grey/20 #E6E6E6) */}
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: 0,
                border: "none",
                borderTop: "1px solid #E6E6E6",
                transform: "rotate(0deg)",
                opacity: 1,
              }}
            />
          </Box>
        )}

        {/* Attribute rows */}
        <Stack divider={<Box sx={{ borderBottom: "1px solid #EEF0F2" }} />} spacing={0}>
          {ATTRIBUTES.map(({ key, label, desc }) => {
            const current = Number(weights?.[key] || 0);
            const baseline = baselineWeights[key];
            const delta = current - baseline;
            const isSaving = savingAttr === key;
            const isEditing = editingAttr === key;
            return (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: { xs: 1.5, sm: 2 },
                  py: 2,
                  position: "relative",
                  bgcolor: isSaving ? "#F0F6FF" : "transparent",
                  transition: "background-color 200ms",
                  borderRadius: 1,
                  minWidth: 0,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                {isSaving && (
                  <Box sx={{ position: "absolute", left: -8, top: 6, bottom: 6, width: 4, borderRadius: 2, bgcolor: "#1A6EF5" }} />
                )}

                <Box
                  sx={{
                    width: { xs: "100%", sm: "clamp(140px, 22%, 208px)" },
                    maxWidth: { xs: "100%", sm: 208 },
                    minWidth: { xs: 0, sm: 140 },
                    minHeight: 59,
                    height: "auto",
                    flex: { xs: "0 0 auto", sm: "0 1 208px" },
                    flexShrink: { xs: 0, sm: 1 },
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    opacity: 1,
                    transform: "rotate(0deg)",
                    boxSizing: "border-box",
                    overflow: "hidden",
                  }}
                >
                  <Typography fontWeight={700} fontSize={14} sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</Typography>
                  <Typography
                    fontSize={12}
                    color="#666"
                    sx={{
                      m: 0,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {desc}
                  </Typography>
                  {isSaving && (
                    <Typography fontSize={11} color="#1A6EF5" sx={{ m: 0, fontWeight: 700 }}>
                      Auto-saving…
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1.5, md: 2, lg: 3 },
                    flex: "1 1 0",
                    minWidth: 0,
                    overflow: "visible",
                    pb: { xs: 0.5, sm: 0 },
                  }}
                >
                  <Box
                    sx={{
                      flex: "1 1 0",
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                      boxSizing: "border-box",
                      overflow: "visible",
                    }}
                  >
                    <Slider
                      min={SLIDER_MIN}
                      max={SLIDER_MAX}
                      step={SLIDER_STEP}
                      value={current}
                      marks={SLIDER_MARKS}
                      size="small"
                      tabIndex={-1}
                      aria-label={`${label} weight indicator`}
                      onChange={() => {}}
                      components={{
                        Thumb: (thumbProps) => (
                          <WeightSliderThumb
                            {...thumbProps}
                            weight={current}
                            tooltipDisabled={POST_GATE0_DISABLED}
                          />
                        ),
                      }}
                      sx={TRUST_SLIDER_SX}
                    />
                  </Box>

                  <Box sx={{ flexShrink: 0, textAlign: "center", minWidth: 64 }}>
                    <Typography
                      fontSize={10}
                      fontWeight={700}
                      color="#98A2B3"
                      sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}
                    >
                      Baseline
                    </Typography>
                    <Typography fontSize={14} fontWeight={700} color="#344054" sx={{ mt: 0.25 }}>
                      {baseline}%
                    </Typography>
                  </Box>

                  <Box sx={{ flexShrink: 0 }}>
                    <DeltaFromBaseline delta={delta} />
                  </Box>

                  <Box sx={{ flexShrink: 0 }}>
                    <WeightInput
                      value={isEditing ? draftValue : String(current)}
                      disabled={POST_GATE0_DISABLED}
                      focused={isEditing}
                      onFocus={handleInputFocus(key, current)}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur(key)}
                      onKeyDown={handleInputKeyDown(key)}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleResetToBaseline}
            disabled={POST_GATE0_DISABLED || !aiRecommendation}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderColor: "#005BF0",
              color: "#005BF0",
              "&:hover": { borderColor: "#005BF0", bgcolor: "rgba(26,110,245,0.04)" },
            }}
          >
            Reset to baseline
          </Button>
        </Box>
      </Box>

      {/* Sticky error toast — total ≠ 100.
          Only fires after weights have been generated at least once; the
          all-zero first-visit state shows the "Generate ideal weights" CTA
          instead, so we don't scream "currently 0%" at the user before they've
          had a chance to act. */}
      <Snackbar
        open={Boolean(aiRecommendation) && Boolean(weights) && !isValidTotal}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" sx={{ fontWeight: 700 }}>
          Total must equal 100% — currently {total}% ({total > 100 ? `+${total - 100}` : total - 100}).
          Adjust the weight fields until the total equals 100%.
        </Alert>
      </Snackbar>

      <Snackbar open={Boolean(error)} onClose={() => setError("")} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
      </Snackbar>
      <Snackbar open={Boolean(toast)} autoHideDuration={3500} onClose={() => setToast("")} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Alert severity="success" onClose={() => setToast("")}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
